import React, { useEffect, useRef, useState } from "react";
import { MdCameraAlt, MdDelete, MdDownload } from "react-icons/md";
import { alertDialog } from "../../../utils/dialogs";

// Campus Analytics Modal Component
function CampusAnalyticsModal({
  isOpen,
  campus,
  dashboardUrl,
  onClose,
  enableDownloadReport = false,
}) {
  const [snapshots, setSnapshots] = useState([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const dashboardFrameRef = useRef(null);

  useEffect(() => {
    if (isOpen) setSnapshots([]);
  }, [campus?.id, dashboardUrl, isOpen]);

  if (!isOpen || !campus || !dashboardUrl) return null;

  const escapeHtml = (value = "") =>
    String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const handleTakeSnapshot = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      alertDialog(
        "Screen capture is not supported in this browser. Please use Chrome or Edge on localhost/HTTPS.",
        { title: "Screen Capture Unsupported" },
      );
      return;
    }

    setIsCapturing(true);

    try {
      dashboardFrameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      await new Promise((resolve) => setTimeout(resolve, 450));

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: "always",
          displaySurface: "browser",
        },
        audio: false,
        preferCurrentTab: true,
        selfBrowserSurface: "include",
        surfaceSwitching: "exclude",
        systemAudio: "exclude",
      });

      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;

      await new Promise((resolve) => {
        video.onloadedmetadata = resolve;
      });
      await video.play();
      await new Promise((resolve) => setTimeout(resolve, 500));

      const dashboardRect = dashboardFrameRef.current?.getBoundingClientRect();
      const scaleX = video.videoWidth / window.innerWidth;
      const scaleY = video.videoHeight / window.innerHeight;
      const sourceX = dashboardRect ? Math.max(0, dashboardRect.left * scaleX) : 0;
      const sourceY = dashboardRect ? Math.max(0, dashboardRect.top * scaleY) : 0;
      const sourceWidth = dashboardRect
        ? Math.min(video.videoWidth - sourceX, dashboardRect.width * scaleX)
        : video.videoWidth;
      const sourceHeight = dashboardRect
        ? Math.min(video.videoHeight - sourceY, dashboardRect.height * scaleY)
        : video.videoHeight;

      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(sourceWidth));
      canvas.height = Math.max(1, Math.round(sourceHeight));
      const context = canvas.getContext("2d");
      context.drawImage(
        video,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        canvas.width,
        canvas.height,
      );

      stream.getTracks().forEach((track) => track.stop());

      setSnapshots((prev) => [
        ...prev,
        {
          id: Date.now(),
          capturedAt: new Date().toLocaleString(),
          dataUrl: canvas.toDataURL("image/jpeg", 0.92),
        },
      ]);
    } catch (error) {
      if (error?.name !== "NotAllowedError") {
        console.error("Failed to capture dashboard snapshot", error);
        alertDialog("Could not capture the dashboard snapshot.", { title: "Snapshot Failed" });
      }
    } finally {
      setIsCapturing(false);
    }
  };

  const handleRemoveSnapshot = (snapshotId) => {
    setSnapshots((prev) => prev.filter((snapshot) => snapshot.id !== snapshotId));
  };

  const handleDownloadReport = () => {
    if (snapshots.length === 0) {
      alertDialog("Take at least one dashboard snapshot before downloading.", { title: "No Snapshots" });
      return;
    }

    const reportWindow = window.open("", "_blank");

    if (!reportWindow) {
      window.print();
      return;
    }

    reportWindow.opener = null;

    const logoUrl = `${window.location.origin}${process.env.PUBLIC_URL}/als-logo.png`;
    const generatedAt = new Date().toLocaleString();
    const campusName = escapeHtml(campus.name);
    const location = escapeHtml(campus.location || "N/A");
    const principal = escapeHtml(campus.principal || "Not Assigned");
    const snapshotsMarkup = snapshots
      .map(
        (snapshot, index) => `
          <section class="snapshot-card">
            <div class="snapshot-heading">
              <h3>Snapshot ${index + 1}</h3>
              <span>${escapeHtml(snapshot.capturedAt)}</span>
            </div>
            <img src="${snapshot.dataUrl}" alt="Dashboard snapshot ${index + 1}" />
          </section>
        `,
      )
      .join("");

    reportWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>${campusName} Analytics Report</title>
          <style>
            * { box-sizing: border-box; }
            body {
              margin: 0;
              padding: 18px 28px;
              background: #ffffff;
              color: #0f1d36;
              font-family: "Segoe UI", Arial, sans-serif;
            }
            .topline {
              display: grid;
              grid-template-columns: 1fr auto 1fr;
              align-items: center;
              gap: 12px;
              font-size: 10px;
              font-weight: 700;
              color: #0f1d36;
            }
            .topline span:nth-child(2) { text-align: center; }
            .topline span:last-child { text-align: right; }
            .red-rule {
              height: 6px;
              margin: 7px 0 22px;
              background: #d4272e;
            }
            .report-shell {
              max-width: 1120px;
              margin: 0 auto;
              background: #ffffff;
            }
            .report-header {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 20px;
              padding: 0 42px 24px;
              border-bottom: 1px solid #dbe3ef;
            }
            .brand {
              display: flex;
              align-items: center;
              gap: 26px;
            }
            .brand img {
              width: 118px;
              height: 74px;
              object-fit: contain;
              background: #ffffff;
            }
            .report-title-kicker {
              color: #d4272e;
              font-size: 12px;
              font-weight: 900;
              letter-spacing: 3px;
              text-transform: uppercase;
            }
            .brand h1,
            .section-title {
              margin: 0;
              color: #0f1d36;
              font-family: Georgia, "Times New Roman", serif;
              font-size: 42px;
              line-height: 0.96;
            }
            .report-meta {
              text-align: right;
              color: #0f1d36;
              font-size: 14px;
              line-height: 1.8;
              padding-top: 22px;
            }
            .report-meta strong {
              font-weight: 900;
            }
            .campus-info-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 18px;
              padding: 28px 42px;
              border-bottom: 1px solid #e5e7eb;
            }
            .campus-info-card {
              min-height: 86px;
              padding: 18px 20px;
              border: 1px solid #d4dfec;
              border-radius: 8px;
              background: #ffffff;
            }
            .campus-info-card span {
              display: block;
              margin-bottom: 10px;
              color: #0f1d36;
              font-size: 11px;
              font-weight: 900;
              letter-spacing: 1.5px;
              text-transform: uppercase;
            }
            .campus-info-card strong {
              color: #0f1d36;
              font-size: 16px;
            }
            .snapshots-section {
              padding: 24px 42px 36px;
            }
            .section-title {
              margin-bottom: 18px;
              font-size: 28px;
              line-height: 1.2;
            }
            .snapshot-card {
              padding: 18px 22px 22px;
              border: 1px solid #d4dfec;
              border-radius: 10px;
              margin-bottom: 28px;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .snapshot-heading {
              display: flex;
              justify-content: space-between;
              gap: 18px;
              align-items: center;
              margin-bottom: 14px;
            }
            .snapshot-heading h3 {
              margin: 0;
              color: #0f1d36;
              font-family: Georgia, "Times New Roman", serif;
              font-size: 22px;
            }
            .snapshot-heading span {
              color: #0f1d36;
              font-size: 12px;
              font-weight: 800;
            }
            .snapshot-card img {
              width: 100%;
              display: block;
              border: 1px solid #d4dfec;
              border-radius: 8px;
            }
            @media print {
              body {
                padding: 0;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              @page {
                margin: 14mm 12mm;
              }
              .snapshot-card {
                margin-bottom: 24px;
              }
            }
            @media (max-width: 760px) {
              .topline {
                grid-template-columns: 1fr;
              }
              .topline span,
              .topline span:nth-child(2),
              .topline span:last-child {
                text-align: left;
              }
              .report-header,
              .brand {
                flex-direction: column;
              }
              .report-meta {
                text-align: left;
                padding-top: 0;
              }
              .campus-info-grid {
                grid-template-columns: 1fr;
              }
            }
          </style>
        </head>
        <body>
          <main class="report-shell">
            <div class="topline">
              <span>${escapeHtml(generatedAt)}</span>
              <span>${campusName} Analytics Report</span>
              <span></span>
            </div>
            <div class="red-rule"></div>
            <header class="report-header">
              <div class="brand">
                <img src="${logoUrl}" alt="American Lycetuff" />
                <div>
                  <div class="report-title-kicker">American Lycetuff</div>
                  <h1>Campus<br />Analytics<br />Report</h1>
                </div>
              </div>
              <div class="report-meta">
                <div><strong>Campus:</strong> ${campusName}</div>
                <div><strong>Generated:</strong> ${escapeHtml(generatedAt)}</div>
              </div>
            </header>
            <section class="campus-info-grid">
              <div class="campus-info-card">
                <span>Campus Name</span>
                <strong>${campusName}</strong>
              </div>
              <div class="campus-info-card">
                <span>Location</span>
                <strong>${location}</strong>
              </div>
              <div class="campus-info-card">
                <span>Principal</span>
                <strong>${principal}</strong>
              </div>
            </section>
            <section class="snapshots-section">
              <h2 class="section-title">Power BI Dashboard Snapshots</h2>
              ${snapshotsMarkup}
            </section>
          </main>
          <script>
            window.addEventListener("load", function () {
              setTimeout(function () { window.print(); }, 900);
            });
          </script>
        </body>
      </html>
    `);
    reportWindow.document.close();
  };

  return (
    <div className="modal-overlay analytics-overlay">
      <div className="modal-content analytics-modal-content">
        <div className="modal-header analytics-modal-header">
          <h3>{campus.name} - Power BI Dashboard</h3>
          <div className="analytics-modal-actions">
            {enableDownloadReport && (
              <button
                type="button"
                className="analytics-download-btn"
                onClick={handleTakeSnapshot}
                disabled={isCapturing}
              >
                <MdCameraAlt /> {isCapturing ? "Capturing..." : "Take Snapshot"}
              </button>
            )}
            {enableDownloadReport && (
              <button
                type="button"
                className="analytics-download-btn"
                onClick={handleDownloadReport}
                disabled={snapshots.length === 0}
              >
                <MdDownload /> Done & Download PDF
              </button>
            )}
            <button type="button" className="modal-close-btn" onClick={onClose}>
              &times;
            </button>
          </div>
        </div>
        <div className="analytics-iframe-wrap" ref={dashboardFrameRef}>
          <iframe
            title={`${campus.name} Power BI Dashboard`}
            src={dashboardUrl}
            allowFullScreen
          />
        </div>
        {enableDownloadReport && (
          <div className="analytics-snapshot-bar">
            <span>{snapshots.length} snapshot{snapshots.length === 1 ? "" : "s"} captured</span>
            <p>
              Apply filters in Power BI, choose This Tab when prompted, not Window
              or Entire Screen, then snapshots will crop to the dashboard area.
            </p>
          </div>
        )}
        {enableDownloadReport && snapshots.length > 0 && (
          <div className="analytics-snapshot-tray">
            {snapshots.map((snapshot, index) => (
              <div className="analytics-snapshot-pill" key={snapshot.id}>
                <span>Snapshot {index + 1}</span>
                <small>{snapshot.capturedAt}</small>
                <button
                  type="button"
                  onClick={() => handleRemoveSnapshot(snapshot.id)}
                  aria-label={`Remove snapshot ${index + 1}`}
                >
                  <MdDelete />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CampusAnalyticsModal;
